import useSWR from 'swr';
import { useMemo } from 'react';
import axiosInstance, { fetcher, revalidateOnlyStaleOptions } from 'src/utils/axios';
import { cleanParams, getURL } from 'src/utils/url';
import { ENDPOINTS } from './endpoints';
import { isEmpty } from 'src/utils/type_check';

export const PRINT_CATEGORY = {
  LABEL: 'Label',
  DOCUMENT: 'Document',
};

export const SELECT_MODE = {
  SEARCH: 'search',
  SCAN: 'scan',
  EXCEL: 'excel',
};

export const catalogKey = (item) => (item?.isTemplate ? item?.labelTemplateId : item?.code);

export function useGetPrintCatalog(printCategory) {
  const URL = ENDPOINTS.print.get_catalog;
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, revalidateOnlyStaleOptions);

  const memoizedValue = useMemo(() => {
    const list = data?.result?.list || [];
    return {
      searchResults: printCategory
        ? list.filter((item) => item.printCategory === printCategory)
        : list,
      searchLoading: isLoading,
      searchError: error,
      searchValidating: isValidating,
    };
  }, [data?.result, error, isLoading, isValidating, printCategory]);
  return memoizedValue;
}

export function useGetPrintTargetSearch(params) {
  const URL = isEmpty(params?.printType)
    ? null
    : getURL(ENDPOINTS.print.get_target_search, cleanParams({ ...params }));
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, revalidateOnlyStaleOptions);

  const memoizedValue = useMemo(
    () => ({
      searchResults: data?.result?.list || [],
      searchCount: data?.result?.count || { allCount: 0 },
      searchLoading: isLoading,
      searchError: error,
      searchValidating: isValidating,
    }),
    [data?.result, error, isLoading, isValidating],
  );
  return memoizedValue;
}

export function useGetPrintSample(params) {
  const URL = isEmpty(params?.printType)
    ? null
    : getURL(ENDPOINTS.print.get_sample, cleanParams({ ...params }));
  const { data, isLoading, error } = useSWR(URL, fetcher, revalidateOnlyStaleOptions);

  const memoizedValue = useMemo(
    () => ({
      sample: data?.result ?? null,
      sampleLoading: isLoading,
      sampleError: error,
    }),
    [data?.result, error, isLoading],
  );
  return memoizedValue;
}

export async function getPrintTargetSearch(params) {
  const ret = await axiosInstance.get(
    getURL(ENDPOINTS.print.get_target_search, cleanParams({ ...params })),
  );
  return ret.data;
}

export async function getPrintHistory(params) {
  const ret = await axiosInstance.get(
    getURL(ENDPOINTS.print.get_history, cleanParams({ ...params })),
  );
  return ret.data;
}

export function useGetPrintHistory(params) {
  const URL = isEmpty(params?.printType)
    ? null
    : getURL(ENDPOINTS.print.get_history, cleanParams({ ...params }));
  const { data, isLoading, error } = useSWR(URL, fetcher, revalidateOnlyStaleOptions);

  const memoizedValue = useMemo(
    () => ({
      historyResults: data?.result?.list || [],
      historyLoading: isLoading,
      historyError: error,
    }),
    [data?.result, error, isLoading],
  );
  return memoizedValue;
}

export async function getPrintSample(params) {
  const ret = await axiosInstance.get(
    getURL(ENDPOINTS.print.get_sample, cleanParams({ ...params })),
  );
  return ret.data;
}

export async function getPrintTargetBoxList(bulkShipmentId) {
  const ret = await axiosInstance.get(
    getURL(ENDPOINTS.print.get_target_box_list, { bulkShipmentId }),
  );
  return ret.data;
}

export async function postLabelPrint(data) {
  const ret = await axiosInstance.post(ENDPOINTS.label.post_print, data);
  return ret.data;
}

export async function downloadLabelExcelForm({ printType, labelTemplateId, fileName }) {
  const URL = labelTemplateId
    ? ENDPOINTS.label.get_template_excel_form(labelTemplateId)
    : ENDPOINTS.label.get_print_excel_form(printType);
  const response = await axiosInstance.get(URL, { responseType: 'blob' });

  const blob = new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = `${fileName}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(link.href);
}

export async function postLabelPrintExcel(formData) {
  const ret = await axiosInstance.post(ENDPOINTS.label.post_print_excel, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return ret.data;
}

export async function postPrintReprint(printHistoryId, params) {
  const ret = await axiosInstance.post(
    getURL(ENDPOINTS.print.post_reprint(printHistoryId), params),
  );
  return ret.data;
}
